import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  DataFormActionsComponent,
  DataFormCardComponent,
  DataFormGridComponent,
  ErrorBlockComponent,
} from '@clinic-platform/ui-components';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { OperationsSettingsStateService } from './operations-settings-state.service';
import { SETTINGS_TAB_FORM_STYLES } from './settings-tab-form.styles';

@Component({
  selector: 'app-operations-settings-tab',
  standalone: true,
  imports: [
    DataFormActionsComponent,
    DataFormCardComponent,
    DataFormGridComponent,
    ErrorBlockComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  template: `
    <lib-data-form-card>
      <h3>Operations Settings</h3>
      <p class="settings-tab__hint">
        {{ contextText }}
      </p>
      <p class="settings-tab__hint">
        Slot duration updates are non-retroactive and apply only to newly generated slots.
      </p>
      <p class="settings-tab__hint">
        Scheduling timezone controls how working hours, slot generation, and future rollout dates are interpreted.
      </p>
      @if (state.pendingSlotDurationBannerText) {
        <div class="settings-tab__warning">
          {{ state.pendingSlotDurationBannerText }}
        </div>
      } @else if (state.isSlotDurationChanged) {
        <div class="settings-tab__warning">
          Existing slots will keep {{ state.initialSlotDurationMinutes }} min.
          New slots will be generated with {{ state.currentSlotDurationMinutes }} min.
        </div>
      }

      <form [formGroup]="state.form" (ngSubmit)="save()">
        <lib-data-form-grid>
          <label>
            Slot duration (minutes)
            <input type="number" formControlName="slotDurationMinutes" min="5" max="180"
              [readOnly]="state.form.controls.cancelPendingSlotDurationChange.value" />
          </label>

          <label>
            Generate days ahead
            <input type="number" formControlName="generateDaysAhead" min="1" max="90" />
          </label>

          <label>
            Scheduler interval (minutes)
            <input type="number" formControlName="schedulerIntervalMinutes" min="5" max="1440" />
          </label>

          <label class="settings-tab__checkbox">
            <input type="checkbox" formControlName="schedulerEnabled" />
            Scheduler enabled
          </label>

          <label>
            Appointment completion mode
            <select formControlName="appointmentCompletionMode">
              <option value="auto">Auto-complete past appointments</option>
              <option value="manual_with_checkin">Manual check-in / finish later</option>
            </select>
          </label>

          <label>
            Clinic label
            <input type="text" formControlName="clinicLabel" maxlength="64" />
          </label>

          <label>
            Procedure label
            <input type="text" formControlName="procedureTypeLabel" maxlength="64" />
          </label>

          <label>
            Public app base URL
            <input type="url" formControlName="publicAppBaseUrl"
              [readOnly]="state.usesInheritedPublicAppBaseUrl"
              [placeholder]="state.usesInheritedPublicAppBaseUrl ? 'Not configured' : 'https://app.example.com'" />
            @if (state.form.controls.publicAppBaseUrl.invalid && state.form.controls.publicAppBaseUrl.touched) {
              <span>Invalid HTTP(S) URL</span>
            }
          </label>

          @if (companyId) {
            <label class="settings-tab__checkbox">
              <input type="checkbox" formControlName="useGlobalPublicAppBaseUrl"
                (change)="state.onUseGlobalPublicAppBaseUrlChange($any($event.target).checked)" />
              Use platform public app URL
            </label>
          }

          <label>
            Scheduling timezone region
            <select [disabled]="!state.canSaveSettings" [value]="state.selectedTimeZoneRegion" (change)="state.onTimeZoneRegionChange($any($event.target).value)">
              @for (region of state.timeZoneRegions; track region) {
                <option [value]="region">{{ region }}</option>
              }
            </select>
          </label>

          <label>
            Scheduling timezone
            <select formControlName="timeZone" (change)="state.onTimeZoneValueChange($any($event.target).value)">
              <option value="">Select timezone</option>
              @for (item of state.timeZoneOptions; track item.id) {
                <option [value]="item.id">{{ item.label }}</option>
              }
            </select>
          </label>
        </lib-data-form-grid>

        @if (state.pendingSlotDurationMinutes) {
          <label class="settings-tab__checkbox">
            <input type="checkbox" formControlName="cancelPendingSlotDurationChange"
              (change)="state.onCancelPendingSlotDurationChange($any($event.target).checked)" />
            Cancel scheduled slot duration change
          </label>
        }

        <lib-data-form-actions>
          <button type="submit" [disabled]="state.form.invalid || !state.canSaveSettings">
            {{ state.saving ? 'Saving...' : 'Save operations settings' }}
          </button>
        </lib-data-form-actions>
      </form>

      @if (state.sourceText) {
        <div class="settings-tab__meta">Settings source: {{ state.sourceText }}</div>
      }
      @if (state.getPublicAppBaseUrlSourceText(companyId); as publicAppBaseUrlSourceText) {
        <div class="settings-tab__meta">Public app base URL: {{ publicAppBaseUrlSourceText }}</div>
      }
      @if (state.error) {
        <lib-error-block variant="error" [message]="(state.error | t) ?? ''" />
      }

      @if (state.success) {
        <lib-error-block variant="success" [message]="state.success" />
      }
    </lib-data-form-card>
  `,
  styles: [SETTINGS_TAB_FORM_STYLES],
})
export class OperationsSettingsTabComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() public companyId: string | undefined;
  @Input() public contextText = '';

  protected readonly state = inject(OperationsSettingsStateService);

  public ngOnInit(): void
  {
    this.state.load(this.companyId);
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['companyId'] && !changes['companyId'].firstChange) {
      this.state.load(this.companyId);
    }
  }

  protected save(): void
  {
    this.state.save(this.companyId);
  }

  public ngOnDestroy(): void {
    this.state.invalidateSettings();
  }
}
